import React, { useEffect, useState } from 'react';
import Icon from '@/components/Icon';
import { ApiError, api } from '@/lib/api';

/**
 * Создание / редактирование предмета внеурочных занятий. Минимум полей:
 * название, описание и архив. Без 1С — внеурочка по ФГОС бесплатная.
 */
const EMPTY = { name: '', description: '', archive: false };

export default function ExtracurricularSubjectEditModal({ mode, subject, onClose, onSaved }) {
  const [data, setData]     = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (mode === 'edit' && subject) {
      setData({
        name:        subject.name || '',
        description: subject.description || '',
        archive:     !!subject.archive,
      });
    } else {
      setData(EMPTY);
    }
  }, [mode, subject?.id]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, saving]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError(null);
    try {
      const payload = {
        name:        data.name.trim(),
        description: data.description.trim(),
        archive:     data.archive,
      };
      const res = mode === 'create'
        ? await api.post('/api/extracurricular-subjects', payload)
        : await api.put(`/api/extracurricular-subjects/${subject.id}`, payload);
      onSaved?.(res.subject);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Не удалось сохранить');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sd-overlay" onClick={(e) => { if (e.target.classList.contains('sd-overlay') && !saving) onClose(); }}>
      <div className="sd-modal sd-modal--narrow" role="dialog" aria-modal="true">
        <div className="sd-edit__head">
          <h2>{mode === 'create' ? 'Новый предмет (внеурочка)' : 'Редактирование предмета'}</h2>
          <button className="sd-close" onClick={onClose} disabled={saving}><Icon name="x" size={14} /></button>
        </div>

        <form onSubmit={submit} className="sd-edit__form" noValidate>
          <div className="sd-edit__body">
            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Название *</label>
              <input
                className="sd-edit__input"
                value={data.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="например, «Робототехника» или «Шахматный клуб»"
                required
                autoFocus
                maxLength={255}
              />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="sd-edit__label">Описание</label>
              <textarea
                className="sd-edit__input"
                style={{ height: 'auto', minHeight: 60, padding: 8, resize: 'vertical' }}
                value={data.description}
                onChange={(e) => set('description', e.target.value)}
                placeholder="Краткое описание (необязательно)"
                rows={2}
                maxLength={2000}
              />
            </div>

            <div className="sd-edit__field sd-edit__field--full">
              <label className="ref-checkbox">
                <input
                  type="checkbox"
                  checked={data.archive}
                  onChange={(e) => set('archive', e.target.checked)}
                />
                <span>В архиве (скрыт из выпадашек выбора)</span>
              </label>
            </div>
          </div>

          <div className="sd-edit__foot">
            {error && <div className="sd-edit__error">{error}</div>}
            <div className="sd-edit__foot-spacer" />
            <button type="button" className="btn btn--sm" onClick={onClose} disabled={saving}>Отмена</button>
            <button type="submit" className="btn btn--primary btn--sm" disabled={saving}>
              {saving ? 'Сохраняем…' : (mode === 'create' ? 'Создать' : 'Сохранить')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
